import { usePrevious, useRerender } from "@react-hookz/web";
import { ThemeKey } from "@smartsheet/design-tokens";
import { contains, getClientId, callAllHandlers, createContext, useForkRef } from "@smartsheet/lodestar-utils";
import React, {
    forwardRef,
    HTMLAttributes,
    KeyboardEventHandler,
    MouseEvent,
    MouseEventHandler,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
} from "react";
import { useTheme } from "../../../theme/utils/useTheme";
import { focusNewElement } from "../../../util/focus/focusNewElement";
import { Blanket } from "../../blanket";
import { useFocusMode } from "../../focusMode";
import { useFocusEventListener } from "../../focusMode/hooks/useFocusEventListener";
import { MenuParentContextShape, useMenuParentContext } from "../context/MenuParentContext";
import { FocusDelegate } from "../hooks/FocusDelegate";
import { MenuContainerStyled } from "../styled/MenuContainerStyled";
import { MenuPlacement } from "../types/MenuPlacement";

export type FocusStrategy =
    | "none" // used for when the user clicks the target manually.
    | "first" // when the user presses the down arrow while the target is focused.
    | "last"; // when the user presses the up arrow while the target is focused.

const elementIsScrollable = (elem: HTMLElement | null) => !!elem && elem.offsetHeight < elem.scrollHeight;

export interface MenuContainerProps {
    /** When true, the menu will be a minimum of 240px wide. False by default for now, though this
     * will likely change to true in a future release.
     * Can be overwritten by the `--sds--menu--min-width` CSS variable.
     */
    applyMinWidth: boolean;
    /**
     * If passed, this will be propagated to various parts of this menu.
     */
    clientId: string | undefined;

    /**
     * A flag to tell the Menu which element to focus when after it renders.
     */
    focusStrategy: FocusStrategy;

    /**
     * If true, we'll add a Blanket component behind the menu to block click interactions while it's
     * open.
     */
    includeBlanket: boolean;

    /**
     * If true, will cause ArrowDown on the last menuitem to focus the first menuitem,
     * and visa-versa for ArrowUp on the first menuitem.
     */
    loop: boolean;

    /**
     * Function that will be called when the menu wants to open.
     */
    onCloseRequested: () => void;

    /**
     * The parent type of this menu, if any.
     */
    parent: MenuParentContextShape["parent"];

    /**
     * The placement of the Menu relative to its target element. This is the same as Flyout's
     * placement.
     */
    placement: MenuPlacement;
}

// Tanner: Well, I don't really like this but without being able to pass arbitrary props
// to the Flyout's FlyoutContainer override, we can't get props into the MenuContainer
// any other way.
// TODO We should follow-up with this once the overrides system in Flyout is augmented to
// TODO allow passing a Component AND extra props to it. Tanner did a BUNCH of ts work to
// TODO make doing that type-safe and didn't get it to work. Other component libs that have
// TODO overrides just use any everywhere to get around it. We'll probably have to do the same.
const defaultValue = {
    applyMinWidth: false,
    clientId: undefined,
    parent: false,
    placement: "bottom-start",
    loop: true,
    focusStrategy: "none",
    onCloseRequested: () => undefined,
    includeBlanket: false,
} as const;
export const MenuContainerContext = createContext<MenuContainerProps>(defaultValue, "MenuContainerContext");

const keyCodes = new Set([] as const);
const keyEvents = new Set([] as const);
const mouseEvents = new Set(["mousemove"] as const);

export const MenuContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ children, onKeyDown, onMouseLeave, ...props }, parentRef) => {
        const { applyMinWidth, clientId, placement, loop, focusStrategy, onCloseRequested, parent, includeBlanket } =
            useContext(MenuContainerContext);
        const { closeMenuRootRequested, layout } = useMenuParentContext();
        const [dir] = placement.split("-");

        // Used to manage focus interactions inside the Menu.
        const { current: focusDelegate } = useRef(new FocusDelegate());

        const callbackRef = useCallback(
            (newElement: HTMLDivElement | null) => {
                if (newElement) {
                    focusDelegate.setFocusables(newElement);
                }
            },
            [focusDelegate]
        );
        const { key } = useTheme();
        const themeMode = key as ThemeKey;

        useLayoutEffect(() => {
            // This can be set on the initial render, or it can initially be "none"
            // and then change to first/last if the mouse clicked the target and
            // then pressed up/down to select a menuitem.
            switch (focusStrategy) {
                case "first": {
                    focusNewElement(null, focusDelegate.getFirstElement() as HTMLElement);
                    break;
                }
                case "last": {
                    focusNewElement(null, focusDelegate.getLastElement() as HTMLElement);
                    break;
                }
            }
        }, [focusDelegate, focusStrategy]);

        const localRef = useRef<HTMLDivElement>(null);
        const ref = useForkRef(parentRef, localRef, callbackRef);

        // If we add or remove a scrollbar, we'll re-render. Normally we'd have to
        // add a window onresize listener as well, but popper will already re-render
        // when a resize occurs.
        const isScrollable = elementIsScrollable(localRef.current);
        const previousIsScrollable = usePrevious(isScrollable);
        const rerender = useRerender();
        useLayoutEffect(() => {
            if (previousIsScrollable !== isScrollable) {
                rerender();
            }
        }, [isScrollable, previousIsScrollable, rerender]);

        // In React <=16 handlers are all attached at the doc-level, meaning our local handlers
        // to this component will be called well after the time when they are able to prevent
        // the browser's standard key handling for keys that can cause scrolling.
        // Because we want these keys to do something in the Menu, we need to block them
        // from propagating to the document (window) level handlers.
        useEffect(() => {
            const disableWindowKeyHandling = (event: KeyboardEvent) => {
                switch (event.key) {
                    // TODO we probably need to handle PageUp/PageDown here too, but we can add that later.
                    case "Home":
                    case "End":
                        event.stopPropagation();
                        event.preventDefault();
                        break;
                }
            };
            window.addEventListener("keydown", disableWindowKeyHandling);
            return () => {
                window.removeEventListener("keydown", disableWindowKeyHandling);
            };
        }, []);

        // Handles key navigation between menuitems in the menu.
        const localOnKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
            (event) => {
                if (event.defaultPrevented) {
                    return;
                }

                const activeElement = document.activeElement as HTMLElement;
                if (!activeElement) {
                    return;
                }

                switch (event.key) {
                    case "Escape":
                        onCloseRequested();
                        // Dont' stop propagation here, each menu will close itself in reverse.
                        break;
                    case "Tab":
                        // This will definitely be defined, it's just the typings that say it can be
                        // nullable since the top-level context CAN actually be nullable.
                        closeMenuRootRequested?.();
                        // We need to prevent this or the focus will move along to the start of the DOM
                        // again. We portal this component out to the end of the body.
                        event.preventDefault();
                        break;
                    case "ArrowDown":
                        {
                            const below = focusDelegate.getElementBelow(activeElement);
                            if (below) {
                                focusNewElement(activeElement, below);
                            } else if (loop) {
                                // Loop around to the top.
                                focusNewElement(activeElement, focusDelegate.getFirstElement() as HTMLElement);
                            }
                            event.stopPropagation();
                            event.preventDefault();
                        }

                        break;
                    case "ArrowUp":
                        {
                            const above = focusDelegate.getElementAbove(activeElement);
                            if (above) {
                                focusNewElement(activeElement, above);
                            } else if (loop) {
                                // Loop around to the bottom.
                                focusNewElement(activeElement, focusDelegate.getLastElement() as HTMLElement);
                            }
                            event.stopPropagation();
                            event.preventDefault();
                        }
                        break;
                    case "ArrowRight":
                        // If submenu is in that direction, open it.
                        // If submenu is in opposite direction, close it.
                        if (dir === "left") {
                            onCloseRequested();
                            event.stopPropagation();
                        }
                        break;
                    case "ArrowLeft":
                        // If submenu is in that direction, open it.
                        // If submenu is in opposite direction, close it.
                        if (dir === "right") {
                            onCloseRequested();
                            event.stopPropagation();
                        }

                        break;
                    case "Home": {
                        focusNewElement(activeElement, focusDelegate.getFirstElement() as HTMLElement);
                        event.stopPropagation();
                        break;
                    }

                    case "End": {
                        focusNewElement(activeElement, focusDelegate.getLastElement() as HTMLElement);
                        event.stopPropagation();
                        break;
                    }
                }
            },
            [onCloseRequested, closeMenuRootRequested, dir, focusDelegate, loop]
        );

        const combinedOnKeyDown: KeyboardEventHandler<HTMLDivElement> = callAllHandlers(onKeyDown, localOnKeyDown);

        // When the mouse leaves a menu, blur the currently focused item.
        // Managing focus like this might seem weird, but the focus mode isn't changed on mousemove
        // as of lodestar-core v1.69.0. When we force focus out of the menu in this component, we
        // want to set focusMode back to mouse so things are sync'd properly. Focus goes to body when
        // you blur the active element. See the onMouseLeave handler below.
        // Another reason we do this is to allow the onMouseEnter handler in MenuItemBase
        // to properly handle the focus change. Without resetting this, the hovered
        // menuitem will not receive focus.
        // In this component, we don't listen to any keyboard events, but we do listen to mousemove.
        // This means that when the mouse moves, focusMode goes back to mouse. This breaks away from
        // the usual :focus-visible heuristic but is more closely aligned to how OS menus work.
        const { focusMode, setFocusMode } = useFocusMode();
        useFocusEventListener({
            focusMode,
            setFocusMode,
            keyCodes,
            mouseEvents,
            keyEvents,
        });
        const localOnMouseLeave: MouseEventHandler<HTMLDivElement> = useCallback((event) => {
            if (event.defaultPrevented) {
                return;
            }
            if (document.activeElement && contains(localRef.current, document.activeElement)) {
                (document.activeElement as HTMLElement).blur();
                event.stopPropagation();
            }
        }, []);
        const combinedOnMouseLeave: MouseEventHandler<HTMLDivElement> = callAllHandlers(
            onMouseLeave,
            localOnMouseLeave
        );

        const combinedOnClick = callAllHandlers(onCloseRequested, (e: MouseEvent) => {
            e.stopPropagation();
        });

        return (
            <>
                {includeBlanket &&
                    // Only the root menu gets a blanket, everything else will reuse the root's blanket
                    !parent && (
                        <Blanket
                            onClick={combinedOnClick}
                            // Since this whole component is within a stacking context, setting this to 0
                            // means the blanket will always be under the menu since it's rendered first.
                            zIndex={0}
                            clientId={getClientId(clientId, "blanket")}
                        />
                    )}
                <MenuContainerStyled
                    {...props}
                    applyMinWidth={applyMinWidth}
                    onKeyDown={combinedOnKeyDown}
                    onMouseLeave={combinedOnMouseLeave}
                    ref={ref}
                    themeKey={themeMode}
                    placement={placement}
                    layout={layout}
                >
                    {children}
                </MenuContainerStyled>
            </>
        );
    }
);

MenuContainer.displayName = "MenuContainer";
